/**
 * "The customer resolution ladder" (docs section 4): the twelve rungs of
 * `resolve_customer_content` in the order in which that function runs them, from a decision
 * of a sales person down to the ambiguity check, each with the name to search for in the
 * code, and the certainty check that files every result as certain, suggested or open.
 * Under the check stands the investigator (13 Oct 2026): a result that is not certain can
 * go to it, and its proposal is a suggestion. On a laptop the check stands beside the
 * ladder; on a phone it stands under it (the narrow media query).
 */

import { ArrowDown, ArrowRight, Icon, Scale, Search, ToggleRight } from "@norican/icons";
import {
  Badge,
  type BadgeTone,
  Card,
  Timeline,
  type TimelineItem,
  Typography,
} from "@norican/react";
import type { ReactNode } from "react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";

import { Figure } from "./figure";

export const CUSTOMER_RESOLUTION_LADDER_TITLE = "The customer resolution ladder";

/** What the two parts of the drawing show, for a screen reader. */
export const LADDER_SUMMARY = "The twelve rungs that find the customer of an inquiry, in order";
export const GATE_SUMMARY = "The certainty check and its three results";
export const INVESTIGATOR_SUMMARY = "The investigator, for a result that is not certain";

/** A rung: its name, the name to search for in the code, and what it does. */
type Rung = readonly [title: string, name: string, text: string];

/**
 * The rungs in the order of `resolve_customer_content` (`quote_agent.py`). Rungs 3 and 4
 * are the two parts of `_customer_flow`; `_rank_stage` runs inside `_stage5`.
 */
export const CUSTOMER_RUNGS: readonly Rung[] = [
  ["A decision of a sales person", "confirmed", "A confirmed customer wins. The ladder stops."],
  [
    "Norican company",
    "_intercompany",
    "A Norican company that buys, read from an attached purchase order. A certain result stops the ladder with no model call.",
  ],
  [
    "Sender address",
    "resolve_customer",
    "An exact contact address or a unique mail domain. Then the earlier quotations for this sender.",
  ],
  [
    "Mail content",
    "extract_identity",
    "At most one model call reads the name of the customer, the end customer and the original sender.",
  ],
  [
    "Customer master",
    "_refine",
    "It reads the master data: a blocked account, a quotation that the mail names, the delivery postcode. It also reads our attached order confirmation and the account number on an attached order.",
  ],
  [
    "Memory",
    "_memory_stage",
    'Who got which quotation before. The pairs are in `rfq_contact_memory`. A sender with the mark "Several accounts" only lists its accounts.',
  ],
  [
    "Evidence families, then rank",
    "_stage5",
    "Two families of evidence, or one strong family, must name the same account. If they do not, `_rank_stage` puts the candidates in order. A marked sender gives no sender link.",
  ],
  [
    "Subject",
    "_subject_stage",
    "A customer that the subject names becomes a suggestion, if no rung found an account.",
  ],
  [
    "Address",
    "_address_stage",
    "It compares the postcodes and the country in the mail with the master address.",
  ],
  [
    "Plant",
    "_plant_stage",
    "A firm has several accounts, also with other names. If the mail names the city or postcode of exactly one, that account is the suggestion. Two accounts with one name and address give no suggestion.",
  ],
  [
    "End customer",
    "_end_customer_quoted",
    "If an agent names an end customer, the account of the agent is never the result. An account of the end customer in the candidates is the suggestion.",
  ],
  [
    "Ambiguity",
    "customer_ambiguity.demote",
    "A certain result becomes a suggestion if the mail also points at another customer.",
  ],
];

export const GATE_TITLE = "The certainty check";
export const GATE_RULE =
  "A new certain result needs two independent signals. One customer with exactly one account is also certain if its name score is 0.70 or more and 0.15 ahead of every other customer.";
// ste-allow: code
export const GATE_NUMBERS = "`FIRM_MIN_SCORE` 0.70, `FIRM_MIN_MARGIN` 0.15, `GATE_MIN_FAMILIES` 2";

/** The three results: the word, its badge tone, and what follows. */
export const GATE_RESULTS: readonly (readonly [string, BadgeTone, string])[] = [
  [
    "certain",
    "success",
    "The `route` is `quote-draft`. The full item ladder runs for this account.",
  ],
  [
    "suggested",
    "warning",
    "A sales person confirms the account. Examples: a mail domain alone, or a contact on several accounts.",
  ],
  ["open", "neutral", "I2Q found no account."],
];
export const GATE_NOT_CERTAIN =
  "For suggested and open, the `route` is `ask-customer`. I2Q accepts no line automatically.";

export const INVESTIGATOR_TITLE = "After the check: the investigator";
/** What the investigator does with a result that is not certain, one sentence a line. */
export const INVESTIGATOR_LINES: readonly string[] = [
  "A suggested or open result can go to the investigator.",
  "A loop with read-only tools reads the mirror: accounts by name, city and postcode, our document numbers, earlier quotations.",
  "A refute step tries to prove the proposal wrong. A refuted proposal is dropped, and a proposal with a doubt is a candidate.",
  // Round 2 (13 Oct 2026): our own document number, the agent pair, and the two answers.
  "One of our document numbers in the mail names the account by code, before the model decides.",
  "On a mail from an agent both accounts are shown: the end customer and the sender.",
  "A sales person confirms the proposal or rejects it. The page shows each lookup that decided.",
  "The proposal is a suggestion. The investigator never makes a customer certain.",
];
export const INVESTIGATOR_SWITCH = "RFQ_MATCHING_INVESTIGATION_MODE";
export const INVESTIGATOR_BADGE = "environment switch";
export const INVESTIGATOR_DEFAULT = "The default is `off`. The mode `observe` stores a trace only.";

export const LADDER_NOTE =
  "The order of the rungs is in `resolve_customer_content` in `backend/src/rfq_foundry/engine/pipeline/quote_agent.py`. The rules of the check are in `mail/customer_rules.py` and `mail/customer_links.py`.";

/** Backtick spans in code font, as the documentation page prints them. */
function code(text: string): ReactNode {
  return text.split("`").map((part, index) =>
    index % 2 ? (
      <Typography key={index} as="code" variant="code" className="text-[0.9em] break-words">
        {part}
      </Typography>
    ) : (
      part
    ),
  );
}

/** `layout` overrides the media query for the render tests. Omit it in the app. */
export function CustomerResolutionLadderFigure(props: {
  caption?: string;
  layout?: "beside" | "stacked";
}) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const form = props.layout ?? (narrow ? "stacked" : "beside");
  const stacked = form === "stacked";
  const rungs: TimelineItem[] = CUSTOMER_RUNGS.map(([title, name, text], index) => ({
    id: name,
    title,
    tone: index === 0 ? "success" : "default",
    icon: (
      <Badge tone="accent" variant="subtle" size="sm" className="tabular-nums">
        {index + 1}
      </Badge>
    ),
    description: (
      <span className="flex min-w-0 flex-col items-start gap-1">
        <span className="break-words">{code(text)}</span>
        <Badge tone="neutral" variant="outline" size="sm" shape="rounded" className="font-mono">
          {name}
        </Badge>
      </span>
    ),
  }));
  return (
    <Figure
      name="customer-resolution-ladder"
      title={CUSTOMER_RESOLUTION_LADDER_TITLE}
      caption={props.caption}
    >
      <div
        data-layout={form}
        className={
          stacked
            ? "flex min-w-0 flex-col gap-3"
            : "grid grid-cols-[minmax(0,5fr)_auto_minmax(0,4fr)] items-center gap-3"
        }
      >
        <Timeline
          orientation="vertical"
          connector="arrow"
          items={rungs}
          aria-label={LADDER_SUMMARY}
        />
        <span className="text-fg-muted flex flex-col items-center text-xs">
          result
          <Icon icon={stacked ? ArrowDown : ArrowRight} size="sm" label="to" />
        </span>
        <div className="flex min-w-0 flex-col gap-3">
          <Card
            variant="outline"
            padding="sm"
            accent="left"
            accentTone="accent"
            role="group"
            aria-label={GATE_SUMMARY}
            className="flex min-w-0 flex-col gap-2"
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <Icon icon={Scale} size="sm" className="text-fg-muted shrink-0" />
              <Typography variant="label">{GATE_TITLE}</Typography>
            </span>
            <Typography variant="body-sm" className="break-words">
              {GATE_RULE}
            </Typography>
            <ul className="flex flex-col gap-2">
              {GATE_RESULTS.map(([word, tone, text]) => (
                <li key={word} className="flex min-w-0 flex-col items-start gap-0.5">
                  <Badge tone={tone} variant="subtle" size="sm">
                    {word}
                  </Badge>
                  <span className="text-fg min-w-0 text-sm break-words">{code(text)}</span>
                </li>
              ))}
            </ul>
            <Typography variant="body-sm" className="break-words">
              {code(GATE_NOT_CERTAIN)}
            </Typography>
            <Typography variant="caption" tone="muted" className="break-words">
              {code(GATE_NUMBERS)}
            </Typography>
          </Card>
          <Card
            variant="outline"
            padding="sm"
            role="group"
            aria-label={INVESTIGATOR_SUMMARY}
            data-investigator
            className="flex min-w-0 flex-col gap-2"
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <Icon icon={Search} size="sm" className="text-fg-muted shrink-0" />
              <Typography variant="label">{INVESTIGATOR_TITLE}</Typography>
            </span>
            <ul className="flex flex-col gap-1">
              {INVESTIGATOR_LINES.map((line) => (
                <li key={line} className="text-fg min-w-0 text-sm break-words">
                  {line}
                </li>
              ))}
            </ul>
            <span className="flex min-w-0 flex-wrap items-center gap-1.5">
              <Badge tone="warning" variant="outline" size="sm">
                <Icon icon={ToggleRight} size="xs" />
                {INVESTIGATOR_BADGE}
              </Badge>
              <Typography as="code" variant="code" className="text-[0.8em] break-all">
                {INVESTIGATOR_SWITCH}
              </Typography>
            </span>
            <Typography variant="caption" tone="muted" className="break-words">
              {code(INVESTIGATOR_DEFAULT)}
            </Typography>
          </Card>
        </div>
      </div>
      <Typography variant="caption" tone="muted" className="break-words">
        {code(LADDER_NOTE)}
      </Typography>
    </Figure>
  );
}
